import { createQueryKeyStore } from '@lukemorales/query-key-factory'
import {
  GetCampaignOrdersQueryVariables,
  GetCampaignQueryVariables,
  GetCardQueryVariables,
  GetCoachingBadgesQueryVariables,
  GetImagesQueryVariables,
  GetItemQueryVariables,
  GetItemsQueryVariables,
  GetLayoutQueryVariables,
  GetLayoutsQueryVariables,
  GetNotificationsQueryVariables,
  GetOrderQueryVariables,
  GetPaginatedRecipientsQueryVariables,
  GetPlanMoveQueryVariables,
  GetRemindersQueryVariables,
  GetSendableCardQueryVariables,
  GetSentCardsQueryVariables,
  GetSharedCampaignsQueryVariables,
  GetSharedContactsQueryVariables,
} from 'src/graphql/generated/graphql'
import {
  CheckoutQueryVariables,
  ProductVariantQueryVariables,
  ProductVariantsQueryVariables,
  SaleorOrderQueryVariables,
} from 'src/saleor_graphql/generated/graphql'
import {
  getAccount,
  getAffiliateCountries,
  getCampaign,
  getCampaignOrders,
  getCard,
  getCheckout,
  getCoachingBadges,
  getCreditsBalance,
  getFlags,
  getFonts,
  getImages,
  getItem,
  getItemCategories,
  getItems,
  getLayout,
  getLayouts,
  getMarketingContent,
  getNotifications,
  getOrder,
  getPaginatedRecipients,
  getPlanMove,
  getProductVariant,
  getProductVariants,
  getReceivedPendingMembershipInvites,
  getReminders,
  getSaleorCustomer,
  getSaleorOrder,
  getSendableCard,
  getSentCards,
  getSharedCampaigns,
  getSharedContacts,
} from './queries/fetching'

export const queryKeyStore = createQueryKeyStore({
  account: {
    detail: {
      queryFn: () => getAccount(),
      queryKey: null,
    },
  },
  affiliate_countries: {
    list: {
      queryFn: () => getAffiliateCountries(),
      queryKey: null,
    },
  },
  card: {
    detail: (variables: GetCardQueryVariables) => ({
      queryFn: () => getCard({ id: variables.id }),
      queryKey: [variables.id],
    }),
    list: (variables: GetSentCardsQueryVariables) => ({
      queryFn: () => getSentCards(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  sendableCard: {
    detail: (variables: GetSendableCardQueryVariables) => ({
      queryFn: () => getSendableCard({ id: variables.id }),
      queryKey: [variables.id],
    }),
  },
  itemCategory: {
    list: {
      queryFn: () => getItemCategories(),
      queryKey: null,
    },
  },
  item: {
    list: (variables: GetItemsQueryVariables) => ({
      queryFn: () => getItems(variables),
      queryKey: [
        {
          filters: {
            categories: variables?.category,
            search: variables?.search,
          },
        },
      ],
    }),
    detail: (variables: GetItemQueryVariables) => ({
      queryFn: () => getItem({ id: variables.id }),
      queryKey: [variables.id],
    }),
  },
  campaign: {
    detail: (variables: GetCampaignQueryVariables) => ({
      queryFn: () => getCampaign(variables),
      queryKey: [variables.id],
    }),
  },
  infinite_campaign_order: {
    list: (variables: GetCampaignOrdersQueryVariables) => ({
      queryFn: () => getCampaignOrders(variables),
      queryKey: [variables.id],
    }),
  },
  recipients: {
    list: (variables: GetPaginatedRecipientsQueryVariables) => ({
      queryFn: () => getPaginatedRecipients(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  flags: {
    list: {
      queryFn: () => getFlags(),
      queryKey: null,
    },
  },
  marketing_content: {
    list: {
      queryFn: () => getMarketingContent(),
      queryKey: null,
    },
  },
  layout: {
    list: (variables: GetLayoutsQueryVariables) => ({
      queryFn: () => getLayouts(variables),
      queryKey: [{ filters: variables }],
    }),
    detail: (variables: GetLayoutQueryVariables) => ({
      queryFn: () => getLayout(variables),
      queryKey: [variables.id],
    }),
  },
  images: {
    list: (variables: GetImagesQueryVariables) => ({
      queryFn: () => getImages(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  credits_balance: {
    detail: {
      queryFn: () => getCreditsBalance(),
      queryKey: null,
    },
  },
  order: {
    detail: (variables: GetOrderQueryVariables) => ({
      queryFn: () => getOrder(variables),
      queryKey: [variables.id],
    }),
  },
  font: {
    list: {
      queryFn: () => getFonts(),
      queryKey: null,
    },
  },
  coaching_badges: {
    list: (variables: GetCoachingBadgesQueryVariables) => ({
      queryFn: () => getCoachingBadges(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  plan_move: {
    detail: (variables: GetPlanMoveQueryVariables) => ({
      queryFn: () => getPlanMove(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  notifications: {
    detail: (variables: GetNotificationsQueryVariables) => ({
      queryFn: () => getNotifications(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  received_pending_membership_invites: {
    detail: {
      queryFn: () => getReceivedPendingMembershipInvites(),
      queryKey: null,
    },
  },
  reminders: {
    detail: (variables: GetRemindersQueryVariables) => ({
      queryFn: () => getReminders(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  shared_campaigns: {
    detail: (variables: GetSharedCampaignsQueryVariables) => ({
      queryFn: () => getSharedCampaigns(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  shared_contacts: {
    detail: (variables: GetSharedContactsQueryVariables) => ({
      queryFn: () => getSharedContacts(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  checkout: {
    detail: (variables: CheckoutQueryVariables) => ({
      queryFn: () => getCheckout(variables),
      queryKey: [variables.id],
    }),
  },
  saleor_customer: {
    detail: {
      queryKey: null,
      queryFn: () => getSaleorCustomer(),
    },
  },
  saleor_product_variant: {
    detail: (variables: ProductVariantQueryVariables) => ({
      queryFn: () => getProductVariant(variables),
      queryKey: [variables],
    }),
  },
  saleor_infinite_product_variants: {
    list: (variables: ProductVariantsQueryVariables) => ({
      queryFn: () => getProductVariants(variables),
      queryKey: ['saleor_infinite_product_variants'],
    }),
  },
  saleor_order: {
    detail: (variables: SaleorOrderQueryVariables) => ({
      queryFn: () => getSaleorOrder(variables),
      queryKey: [variables.id],
    }),
  },
  lines: {
    list: (filters: { checkoutId: string }) => ({
      queryFn: () => getCheckout({ id: filters.checkoutId }),
      queryKey: [{ filters }],
    }),
  },
})
